import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Phone, Car, Settings, ChevronLeft, ChevronRight, X, Plus, Pencil, Trash2, Check, Loader2, Filter, Calendar, Gauge, Fuel, Cog, Clock, MapPin, Truck, CalendarCheck, FileText, LogOut, Lock, Users, Download, Mail, Eye, Shield, Star, ArrowUp, ArrowDown, Image, GripVertical, ExternalLink } from "lucide-react";
import { Toaster, toast } from "sonner";
import { DayPicker } from "react-day-picker";
import { fr } from "date-fns/locale";
import "react-day-picker/dist/style.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert relative image URLs to absolute URLs
const getImageUrl = (img) => {
  const defaultImage = 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=85&w=800&auto=format&fit=crop';
  if (!img) return defaultImage;
  if (img.startsWith('/api/')) {
    return `${BACKEND_URL}${img}`;
  }
  return img;
};

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [email, setEmail] = useState(localStorage.getItem('adminEmail'));
  const [userName, setUserName] = useState(localStorage.getItem('adminName'));
  const [userRole, setUserRole] = useState(localStorage.getItem('adminRole'));

  const login = (newToken, newEmail, newName, newRole) => {
    localStorage.setItem('adminToken', newToken);
    localStorage.setItem('adminEmail', newEmail);
    localStorage.setItem('adminName', newName || '');
    localStorage.setItem('adminRole', newRole || 'client');
    setToken(newToken);
    setEmail(newEmail);
    setUserName(newName);
    setUserRole(newRole);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminRole');
    setToken(null);
    setEmail(null);
    setUserName(null);
    setUserRole(null);
  };

  const isAuthenticated = !!token;
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const isSuperAdmin = userRole === 'super_admin';

  return (
    <AuthContext.Provider value={{ token, email, userName, userRole, login, logout, isAuthenticated, isAdmin, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Axios interceptor for auth
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ==================== HEADER ====================
const Header = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 md:gap-3" data-testid="logo-link">
            <Car className="w-6 h-6 md:w-8 md:h-8 text-[#DC2626]" />
            <span className="font-heading text-xl md:text-2xl font-semibold tracking-tight">
              Larmor<span className="text-red-gradient">Automobile</span>
            </span>
          </Link>
          
          <nav className="flex items-center gap-4 md:gap-8">
            <a 
              href="tel:0685581397" 
              className="hidden md:flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm tracking-wide">06 85 58 13 97</span>
            </a>
            <a 
              href="https://larmorautomobiles.auto-gestion.net/" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#F59E0B] transition-colors"
              data-testid="autogestion-link"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="text-sm">Vitrine</span>
            </a>
            <Link 
              to="/conditions" 
              className="hidden md:flex items-center gap-2 text-[#A1A1AA] hover:text-[#DC2626] transition-colors"
              data-testid="conditions-link"
            >
              <FileText className="w-4 h-4" />
              <span className="text-sm">CGV</span>
            </Link>
            <Link 
              to="/warranty" 
              className="hidden md:flex items-center gap-2 text-[#A1A1AA] hover:text-[#22C55E] transition-colors"
              data-testid="warranty-link"
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm">Garantie</span>
            </Link>
            {isAuthenticated && isAdmin && (
              <Link 
                to="/admin" 
                className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#DC2626] transition-colors"
                data-testid="admin-link"
              >
                <Settings className="w-5 h-5" />
                <span className="hidden md:inline text-sm">Admin</span>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

// ==================== VEHICLE CARD ====================
const VehicleCard = ({ vehicle, index }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  const formatMileage = (km) => {
    return new Intl.NumberFormat('fr-FR').format(km) + ' km';
  };

  return (
    <Link 
      to={`/vehicle/${vehicle.id}`}
      className={`vehicle-card block opacity-0 animate-slide-up stagger-${(index % 6) + 1}`}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <div className="relative overflow-hidden">
        <img 
          src={getImageUrl(vehicle.images?.[0])} 
          alt={`${vehicle.brand} ${vehicle.model}`}
          className="vehicle-image"
        />
        {vehicle.is_reserved && (
          <div className="absolute top-4 right-4 badge-reserved">
            Réservé
          </div>
        )}
      </div>
      <div className="p-4 md:p-5">
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="label-red mb-1">{vehicle.brand}</p>
            <h3 className="font-heading text-lg md:text-xl font-medium text-white">{vehicle.model}</h3>
          </div>
          <p className="text-[#DC2626] font-bold text-lg md:text-xl">{formatPrice(vehicle.price)}</p>
        </div>
        <p className="text-[#71717A] text-sm mb-4 line-clamp-1">{vehicle.version}</p>
        <div className="flex items-center gap-3 md:gap-4 text-xs text-[#A1A1AA]">
          <span className="flex items-center gap-1">
            <Gauge className="w-3 h-3" />
            {formatMileage(vehicle.mileage)}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {vehicle.year}
          </span>
          <span className="flex items-center gap-1">
            <Fuel className="w-3 h-3" />
            {vehicle.fuel}
          </span>
        </div>
      </div>
    </Link>
  );
};

// ==================== HOME PAGE ====================
const Home = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ brand: '', minPrice: '', maxPrice: '' });
  const [showFilters, setShowFilters] = useState(false);

  const fetchVehicles = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      
      const response = await axios.get(`${API}/vehicles?${params.toString()}`);
      setVehicles(response.data);
    } catch (e) {
      console.error('Error fetching vehicles:', e);
      toast.error('Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
    fetchVehicles();
  }, [fetchVehicles]);

  const uniqueBrands = [...new Set(vehicles.map(v => v.brand))];

  return (
    <div className="min-h-screen pt-16 md:pt-20 noise-overlay">
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=85&w=1920&auto=format&fit=crop)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        </div>
        
        <div className="relative z-10 text-center px-4 md:px-6 max-w-4xl mx-auto animate-fade-in">
          <p className="label-red mb-4">Rue des Artisans, Zone de Kervern • 56570 Locmiquélic</p>
          <h1 className="font-heading text-4xl md:text-7xl font-medium mb-4 md:mb-6 leading-none">
            <span className="text-red-gradient">Larmor</span>
            <span className="text-white">Automobile</span>
          </h1>
          <p className="text-[#A1A1AA] text-base md:text-xl max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
            Votre spécialiste automobile. Qualité, Transparence, Garantie.
          </p>
          
          <div className="inline-flex items-center gap-2 bg-[#111]/80 border border-[#DC2626]/30 px-4 py-2 mb-8 backdrop-blur-sm">
            <Truck className="w-4 h-4 text-[#DC2626]" />
            <span className="text-sm text-white">Livraison possible dans toute la France</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#catalogue" className="btn-primary" data-testid="discover-btn">
              Découvrir nos véhicules
            </a>
            <a href="tel:0685581397" className="btn-secondary" data-testid="contact-btn">
              <Phone className="w-4 h-4 inline mr-2" />
              06 85 58 13 97
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-16 px-4 md:px-12 border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#111] p-6 border border-[#27272A] hover:border-[#DC2626]/30 transition-colors">
              <CalendarCheck className="w-8 h-8 text-[#DC2626] mb-4" />
              <h3 className="font-heading text-lg text-white mb-2">RDV Essai Gratuit</h3>
              <p className="text-[#A1A1AA] text-sm">Prenez rendez-vous gratuitement pour essayer nos véhicules. Du lundi au samedi, 9h-19h.</p>
            </div>
            <div className="bg-[#111] p-6 border border-[#27272A] hover:border-[#DC2626]/30 transition-colors">
              <Car className="w-8 h-8 text-[#DC2626] mb-4" />
              <h3 className="font-heading text-lg text-white mb-2">Réservation en ligne</h3>
              <p className="text-[#A1A1AA] text-sm">Réservez votre véhicule avec un acompte de 300€. 7 jours de réflexion garantis.</p>
            </div>
            <div className="bg-[#111] p-6 border border-[#27272A] hover:border-[#DC2626]/30 transition-colors">
              <Truck className="w-8 h-8 text-[#DC2626] mb-4" />
              <h3 className="font-heading text-lg text-white mb-2">Livraison France</h3>
              <p className="text-[#A1A1AA] text-sm">Nous livrons votre véhicule partout en France. Devis sur demande.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Catalogue Section */}
      <section id="catalogue" className="py-12 md:py-20 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6 mb-8 md:mb-12">
            <div>
              <p className="label-red mb-2">Notre sélection</p>
              <h2 className="font-heading text-2xl md:text-4xl font-medium text-white">
                Véhicules disponibles
              </h2>
            </div>
            
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center gap-2"
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4" />
              Filtrer
            </button>
          </div>

          {showFilters && (
            <div className="glass p-4 md:p-6 mb-8 md:mb-10 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                <div>
                  <label className="label-red block mb-2">Marque</label>
                  <select 
                    value={filters.brand}
                    onChange={(e) => setFilters({...filters, brand: e.target.value})}
                    className="w-full bg-[#1A1A1A] text-white border border-[#27272A] px-4 py-3 focus:border-[#DC2626] focus:outline-none"
                    data-testid="filter-brand"
                  >
                    <option value="">Toutes les marques</option>
                    {uniqueBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-red block mb-2">Prix min</label>
                  <input 
                    type="number"
                    placeholder="0 €"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                    className="input-field bg-[#1A1A1A] border border-[#27272A] px-4"
                    data-testid="filter-min-price"
                  />
                </div>
                <div>
                  <label className="label-red block mb-2">Prix max</label>
                  <input 
                    type="number"
                    placeholder="200 000 €"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                    className="input-field bg-[#1A1A1A] border border-[#27272A] px-4"
                    data-testid="filter-max-price"
                  />
                </div>
                <div className="flex items-end">
                  <button 
                    onClick={() => setFilters({ brand: '', minPrice: '', maxPrice: '' })}
                    className="text-[#A1A1AA] hover:text-white transition-colors text-sm"
                    data-testid="filter-reset-btn"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#A1A1AA] text-lg">Aucun véhicule trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {vehicles.map((vehicle, index) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 px-4 md:px-12 border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="label-red mb-4">Une question ?</p>
          <h2 className="font-heading text-2xl md:text-4xl font-medium text-white mb-4 md:mb-6">
            Contactez-nous
          </h2>
          <p className="text-[#A1A1AA] mb-6 md:mb-8 max-w-xl mx-auto">
            Notre équipe est à votre disposition du lundi au samedi, de 9h à 19h.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:0685581397" 
              className="btn-primary inline-flex items-center justify-center gap-3"
              data-testid="contact-cta-btn"
            >
              <Phone className="w-5 h-5" />
              06 85 58 13 97
            </a>
            <Link to="/conditions" className="btn-secondary inline-flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" />
              Conditions de réservation
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 md:py-10 px-4 md:px-6 border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Car className="w-5 h-5 text-[#DC2626]" />
            <span className="font-heading text-lg">Larmor Automobile</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-[#71717A]">
            <Link to="/conditions" className="hover:text-white transition-colors">Conditions</Link>
            <span>•</span>
            <span>Lun-Sam 9h-19h</span>
          </div>
          <p className="text-[#71717A] text-sm">
            © 2025 Larmor Automobile • Rue des Artisans, Zone de Kervern, 56570 Locmiquélic
          </p>
        </div>
      </footer>
    </div>
  );
};

// ==================== IMAGE GALLERY COMPONENT ====================
const ImageGallery = ({ images, vehicleName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const defaultImage = 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=85&w=800&auto=format&fit=crop';
  const displayImages = images?.length > 0 ? images.map(getImageUrl) : [defaultImage];
  
  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };
  
  const goToIndex = (index) => {
    setCurrentIndex(index);
  };

  return (
    <>
      {/* Main Gallery */}
      <div className="relative overflow-hidden bg-[#111] aspect-[4/3] group">
        <img 
          src={displayImages[currentIndex]} 
          alt={`${vehicleName} - Photo ${currentIndex + 1}`}
          className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
          onClick={() => setIsFullscreen(true)}
          data-testid="vehicle-main-image"
        />
        
        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            <button 
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
              data-testid="gallery-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        
        {/* Photo Counter */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {currentIndex + 1} / {displayImages.length}
          </div>
        )}
        
        {/* Fullscreen Button */}
        <button 
          onClick={() => setIsFullscreen(true)}
          className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
          title="Agrandir"
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>
      
      {/* Thumbnails */}
      {displayImages.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {displayImages.map((img, idx) => (
            <button 
              key={idx}
              onClick={() => goToIndex(idx)}
              className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-[#DC2626]' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
              data-testid={`thumbnail-${idx}`}
            >
              <img src={img} alt={`Miniature ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setIsFullscreen(false)}
        >
          <button 
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 text-white hover:text-[#DC2626] p-2 z-10"
          >
            <X className="w-8 h-8" />
          </button>
          
          <img 
            src={displayImages[currentIndex]} 
            alt={`${vehicleName} - Plein écran`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {displayImages.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
              
              {/* Fullscreen Thumbnails */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {displayImages.map((_, idx) => (
                  <button 
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); goToIndex(idx); }}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentIndex ? 'bg-[#DC2626]' : 'bg-white/40 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

// ==================== VEHICLE DETAIL PAGE ====================
const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReservation, setShowReservation] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', acceptTerms: false });
  const [appointmentData, setAppointmentData] = useState({ name: '', email: '', phone: '', date: null, time: '' });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/${id}`);
        setVehicle(response.data);
      } catch (e) {
        toast.error('Véhicule non trouvé');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id, navigate]);

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/appointments/available?date=${dateStr}`);
      setAvailableSlots(response.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des créneaux');
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setAppointmentData({...appointmentData, date, time: ''});
    if (date) {
      fetchAvailableSlots(date);
    }
  };

  const disabledDays = [
    { dayOfWeek: [0] },
    { before: new Date() }
  ];

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  const formatMileage = (km) => {
    return new Intl.NumberFormat('fr-FR').format(km) + ' km';
  };

  const handleReservation = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (!formData.acceptTerms) {
      toast.error('Vous devez accepter les conditions générales de vente');
      return;
    }
    setReservationLoading(true);
    try {
      const response = await axios.post(`${API}/reservations/checkout`, {
        vehicle_id: id,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        origin_url: window.location.origin,
        accept_terms: formData.acceptTerms
      });
      window.location.href = response.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la réservation');
      setReservationLoading(false);
    }
  };

  const handleAppointment = async (e) => {
    e.preventDefault();
    if (!appointmentData.name || !appointmentData.email || !appointmentData.phone || !appointmentData.date || !appointmentData.time) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setAppointmentLoading(true);
    try {
      const dateStr = appointmentData.date.toISOString().split('T')[0];
      await axios.post(`${API}/appointments`, {
        vehicle_id: id,
        customer_name: appointmentData.name,
        customer_email: appointmentData.email,
        customer_phone: appointmentData.phone,
        appointment_date: dateStr,
        appointment_time: appointmentData.time
      });
      toast.success('Rendez-vous confirmé !');
      setShowAppointment(false);
      setAppointmentData({ name: '', email: '', phone: '', date: null, time: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la prise de rendez-vous');
    } finally {
      setAppointmentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!vehicle) return null;

  return (
    <div className="min-h-screen pt-16 md:pt-20 noise-overlay">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-6 md:py-10">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-6 md:mb-8"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Retour au catalogue
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          <div className="animate-fade-in">
            <ImageGallery images={vehicle.images} vehicleName={`${vehicle.brand} ${vehicle.model}`} />
            {vehicle.is_reserved && (
              <div className="mt-3 bg-[#DC2626]/10 border border-[#DC2626]/30 p-3 text-center">
                <span className="text-[#DC2626] font-medium">Véhicule réservé</span>
              </div>
            )}
            
            {/* Warranty Badge */}
            <div className="mt-4 bg-[#22C55E]/10 border border-[#22C55E]/30 p-3 flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#22C55E]" />
              <div>
                <p className="text-[#22C55E] font-medium text-sm">Garantie 6 mois incluse</p>
                <Link to="/warranty" className="text-[#A1A1AA] text-xs hover:text-white">Voir les conditions →</Link>
              </div>
            </div>
          </div>

          <div className="animate-slide-up">
            <p className="label-red mb-2">{vehicle.brand}</p>
            <h1 className="font-heading text-3xl md:text-5xl font-medium text-white mb-2" data-testid="vehicle-title">
              {vehicle.model}
            </h1>
            <p className="text-[#71717A] text-base md:text-lg mb-4 md:mb-6">{vehicle.version}</p>

            <div className="flex items-baseline gap-4 mb-6 md:mb-8">
              <span className="text-[#DC2626] text-3xl md:text-4xl font-bold" data-testid="vehicle-price">
                {formatPrice(vehicle.price)}
              </span>
              {vehicle.market_price && vehicle.market_price > vehicle.price && (
                <span className="text-[#71717A] line-through text-lg">{formatPrice(vehicle.market_price)}</span>
              )}
            </div>

            {/* Caractéristiques principales */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
              <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Kilométrage</p>
                <p className="text-white font-medium flex items-center gap-2 text-sm md:text-base">
                  <Gauge className="w-4 h-4 text-[#DC2626]" />
                  {formatMileage(vehicle.mileage)}
                  {vehicle.guaranteed_mileage && <span className="text-[#22C55E] text-xs">✓</span>}
                </p>
              </div>
              <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Année</p>
                <p className="text-white font-medium flex items-center gap-2 text-sm md:text-base">
                  <Calendar className="w-4 h-4 text-[#DC2626]" />
                  {vehicle.year}
                </p>
              </div>
              <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Carburant</p>
                <p className="text-white font-medium flex items-center gap-2 text-sm md:text-base">
                  <Fuel className="w-4 h-4 text-[#DC2626]" />
                  {vehicle.fuel}
                </p>
              </div>
              <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Boîte</p>
                <p className="text-white font-medium flex items-center gap-2 text-sm md:text-base">
                  <Cog className="w-4 h-4 text-[#DC2626]" />
                  {vehicle.transmission}
                </p>
              </div>
              <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Puissance</p>
                <p className="text-white font-medium text-sm md:text-base">{vehicle.power}</p>
              </div>
              <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Couleur</p>
                <p className="text-white font-medium text-sm md:text-base">{vehicle.color}</p>
              </div>
              {vehicle.doors && (
                <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                  <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Portes</p>
                  <p className="text-white font-medium text-sm md:text-base">{vehicle.doors}</p>
                </div>
              )}
              {vehicle.previous_owners !== undefined && vehicle.previous_owners !== null && (
                <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                  <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Nb. de mains</p>
                  <p className="text-white font-medium text-sm md:text-base">{vehicle.previous_owners === 0 ? '1ère main' : `${vehicle.previous_owners + 1}ème main`}</p>
                </div>
              )}
              {vehicle.vehicle_origin && (
                <div className="bg-[#111] p-3 md:p-4 border border-[#27272A]">
                  <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Origine</p>
                  <p className="text-white font-medium text-sm md:text-base">{vehicle.vehicle_origin}</p>
                </div>
              )}
            </div>

            {/* Historique et contrôles */}
            {(vehicle.last_inspection_date || vehicle.last_service_date || vehicle.recent_fees) && (
              <div className="bg-[#111] p-4 mb-6 border border-[#27272A]">
                <h3 className="label-red mb-3">Historique & Entretien</h3>
                <div className="space-y-2 text-sm">
                  {vehicle.last_inspection_date && (
                    <p className="text-[#A1A1AA]">
                      <span className="text-[#71717A]">Dernier CT :</span> {new Date(vehicle.last_inspection_date).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  {vehicle.last_service_date && (
                    <p className="text-[#A1A1AA]">
                      <span className="text-[#71717A]">Dernière révision :</span> {new Date(vehicle.last_service_date).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  {vehicle.recent_fees && (
                    <p className="text-[#A1A1AA]">
                      <span className="text-[#71717A]">Frais récents :</span> {vehicle.recent_fees}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Badges spéciaux */}
            <div className="flex flex-wrap gap-2 mb-6">
              {vehicle.is_company_vehicle && (
                <span className="bg-[#3B82F6]/20 text-[#3B82F6] px-3 py-1 text-xs border border-[#3B82F6]/30">Véhicule de société</span>
              )}
              {vehicle.guaranteed_mileage && (
                <span className="bg-[#22C55E]/20 text-[#22C55E] px-3 py-1 text-xs border border-[#22C55E]/30">Kilométrage garanti</span>
              )}
            </div>

            <div className="mb-6 md:mb-8">
              <h3 className="label-red mb-3">Description</h3>
              <p className="text-[#A1A1AA] leading-relaxed text-sm md:text-base" data-testid="vehicle-description">
                {vehicle.description}
              </p>
            </div>

            {vehicle.options?.length > 0 && (
              <div className="mb-8 md:mb-10">
                <h3 className="label-red mb-3">Équipements</h3>
                <div className="flex flex-wrap gap-2">
                  {vehicle.options.map((option, i) => (
                    <span key={i} className="bg-[#1A1A1A] text-[#A1A1AA] px-3 py-1 text-xs md:text-sm border border-[#27272A]">
                      {option}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {!vehicle.is_reserved ? (
                <>
                  <button 
                    onClick={() => setShowAppointment(true)}
                    className="btn-outline w-full flex items-center justify-center gap-2"
                    data-testid="appointment-btn"
                  >
                    <CalendarCheck className="w-5 h-5" />
                    Prendre RDV gratuit pour essayer
                  </button>
                  <button 
                    onClick={() => setShowReservation(true)}
                    className="btn-primary w-full"
                    data-testid="reserve-btn"
                  >
                    Réserver ce véhicule (300€ d'arrhes)
                  </button>
                </>
              ) : (
                <button className="btn-primary w-full opacity-50 cursor-not-allowed" disabled>
                  Véhicule réservé
                </button>
              )}
              <a 
                href="tel:0685581397" 
                className="btn-secondary w-full text-center"
                data-testid="call-btn"
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Appeler
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Dialog */}
      {showAppointment && (
        <div className="dialog-overlay" onClick={() => setShowAppointment(false)}>
          <div className="dialog-content animate-slide-up max-w-lg max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="badge-free inline-block mb-2">Gratuit</div>
                <h2 className="font-heading text-xl md:text-2xl text-white">Prendre rendez-vous</h2>
                <p className="text-[#71717A] text-sm mt-1">Essayez ce véhicule sans engagement</p>
              </div>
              <button onClick={() => setShowAppointment(false)} className="text-[#71717A] hover:text-white" data-testid="close-appointment-btn">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-[#1A1A1A] p-4 mb-6 border border-[#27272A]">
              <p className="text-[#71717A] text-sm mb-1">{vehicle.brand} {vehicle.model}</p>
              <p className="text-white font-medium">{formatPrice(vehicle.price)}</p>
            </div>

            <form onSubmit={handleAppointment}>
              <div className="space-y-4">
                <div>
                  <label className="label-red block mb-2">Nom complet</label>
                  <input type="text" value={appointmentData.name} onChange={(e) => setAppointmentData({...appointmentData, name: e.target.value})} className="input-field" placeholder="Jean Dupont" required data-testid="appointment-name" />
                </div>
                <div>
                  <label className="label-red block mb-2">Email</label>
                  <input type="email" value={appointmentData.email} onChange={(e) => setAppointmentData({...appointmentData, email: e.target.value})} className="input-field" placeholder="jean@email.com" required data-testid="appointment-email" />
                </div>
                <div>
                  <label className="label-red block mb-2">Téléphone</label>
                  <input type="tel" value={appointmentData.phone} onChange={(e) => setAppointmentData({...appointmentData, phone: e.target.value})} className="input-field" placeholder="06 12 34 56 78" required data-testid="appointment-phone" />
                </div>
                
                <div>
                  <label className="label-red block mb-2">Choisir une date</label>
                  <div className="calendar-container bg-[#1A1A1A] border border-[#27272A] p-2 flex justify-center">
                    <DayPicker mode="single" selected={appointmentData.date} onSelect={handleDateSelect} disabled={disabledDays} locale={fr} fromDate={new Date()} toDate={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)} className="rdp-custom" />
                  </div>
                  {appointmentData.date && (
                    <p className="text-[#DC2626] text-sm mt-2">
                      Date sélectionnée : {appointmentData.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                  )}
                </div>

                {appointmentData.date && (
                  <div>
                    <label className="label-red block mb-2">Créneau horaire</label>
                    {slotsLoading ? (
                      <div className="flex justify-center py-4"><div className="spinner" /></div>
                    ) : availableSlots.length === 0 ? (
                      <p className="text-[#71717A] text-sm">Aucun créneau disponible</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-2">
                        {availableSlots.map(slot => (
                          <button key={slot.time} type="button" onClick={() => setAppointmentData({...appointmentData, time: slot.time})} className={`time-slot ${appointmentData.time === slot.time ? 'selected' : ''}`} data-testid={`slot-${slot.time}`}>
                            {slot.display}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button type="submit" className="btn-primary w-full mt-6 flex items-center justify-center gap-2" disabled={appointmentLoading || !appointmentData.time} data-testid="submit-appointment-btn">
                {appointmentLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                Confirmer le rendez-vous
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reservation Dialog */}
      {showReservation && (
        <div className="dialog-overlay" onClick={() => setShowReservation(false)}>
          <div className="dialog-content animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="label-red mb-2">Réservation</p>
                <h2 className="font-heading text-xl md:text-2xl text-white">Réserver ce véhicule</h2>
              </div>
              <button onClick={() => setShowReservation(false)} className="text-[#71717A] hover:text-white" data-testid="close-dialog-btn">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-[#1A1A1A] p-4 mb-6 border border-[#27272A]">
              <p className="text-[#71717A] text-sm mb-1">{vehicle.brand} {vehicle.model}</p>
              <p className="text-white font-medium">{formatPrice(vehicle.price)}</p>
              <div className="mt-4 pt-4 border-t border-[#27272A]">
                <p className="text-[#DC2626] font-bold">Arrhes de réservation : 300 €</p>
                <p className="text-[#71717A] text-xs mt-1">Paiement sécurisé par Stripe</p>
                <p className="text-[#22C55E] text-xs mt-1">7 jours pour vous désister</p>
              </div>
            </div>

            <div className="bg-[#1A1A1A]/50 p-3 mb-6 border border-[#27272A]">
              <p className="text-[#A1A1AA] text-xs">
                En réservant, vous acceptez nos <Link to="/conditions" className="text-[#DC2626] underline" target="_blank">conditions de réservation</Link>. 
                Vous disposez d'un délai de <strong className="text-white">7 jours</strong> pour vous désister. Passé ce délai, les arrhes ne seront pas remboursées.
              </p>
            </div>

            <form onSubmit={handleReservation}>
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="label-red block mb-2">Nom complet</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-field" placeholder="Jean Dupont" required data-testid="input-name" />
                </div>
                <div>
                  <label className="label-red block mb-2">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-field" placeholder="jean@email.com" required data-testid="input-email" />
                </div>
                <div>
                  <label className="label-red block mb-2">Téléphone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="input-field" placeholder="06 12 34 56 78" required data-testid="input-phone" />
                </div>
              </div>

              {/* CGV Acceptance Checkbox */}
              <div className="mt-6 p-4 bg-[#1A1A1A]/50 border border-[#27272A]">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({...formData, acceptTerms: e.target.checked})}
                    className="mt-1 w-5 h-5 accent-[#DC2626]"
                    data-testid="accept-terms-checkbox"
                  />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">
                    J'ai lu et j'accepte les <Link to="/conditions" target="_blank" className="text-[#DC2626] underline hover:text-[#EF4444]">Conditions Générales de Vente</Link> et les conditions de la <Link to="/warranty" target="_blank" className="text-[#DC2626] underline hover:text-[#EF4444]">Garantie 6 mois</Link>.
                    Je comprends que je dispose d'un délai de <strong className="text-white">7 jours</strong> pour me rétracter.
                  </span>
                </label>
              </div>

              <button type="submit" className="btn-primary w-full mt-6 flex items-center justify-center gap-2" disabled={reservationLoading || !formData.acceptTerms} data-testid="submit-reservation-btn">
                {reservationLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                Payer les arrhes de 300 €
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DYNAMIC LEGAL PAGE ====================
const LegalPage = ({ pageType }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/legal/${pageType}`);
        setContent(response.data);
      } catch (e) {
        console.error('Error fetching legal page:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [pageType]);

  // Simple markdown-like renderer for content
  const renderContent = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, idx) => {
      // Headers
      if (line.startsWith('## ')) {
        return <h2 key={idx} className="font-heading text-xl text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={idx} className="font-heading text-lg text-white mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      // List items
      if (line.startsWith('- ')) {
        const content = line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
        return <li key={idx} className="ml-4 mb-2" dangerouslySetInnerHTML={{ __html: content }} />;
      }
      // Bold text
      if (line.includes('**')) {
        const content = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
        return <p key={idx} className="mb-4" dangerouslySetInnerHTML={{ __html: content }} />;
      }
      // Empty line
      if (line.trim() === '') {
        return <br key={idx} />;
      }
      // Normal text
      return <p key={idx} className="mb-2">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 md:pt-20 noise-overlay">
      <div className="max-w-4xl mx-auto px-4 md:px-12 py-10 md:py-16">
        <Link to="/" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8">
          <ChevronLeft className="w-5 h-5" />
          Retour
        </Link>

        <div className="animate-fade-in">
          <p className="label-red mb-4">Informations légales</p>
          <h1 className="font-heading text-3xl md:text-4xl text-white mb-8" data-testid="legal-page-title">
            {content?.title || (pageType === 'cgv' ? 'Conditions Générales de Vente' : 'Garantie Commerciale')}
          </h1>

          {pageType === 'warranty' && (
            <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 p-4 mb-8 flex items-center gap-3">
              <div className="w-12 h-12 bg-[#22C55E] flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[#22C55E] font-medium">Garantie 6 mois incluse</p>
                <p className="text-[#A1A1AA] text-sm">Tous nos véhicules sont garantis</p>
              </div>
            </div>
          )}

          <div className="space-y-2 text-[#A1A1AA] prose-content">
            {renderContent(content?.content)}
          </div>

          {/* Contact section */}
          <div className="mt-12 pt-8 border-t border-[#27272A]">
            <h3 className="font-heading text-lg text-white mb-4">Contact</h3>
            <div className="bg-[#111] p-4 border border-[#27272A]">
              <p className="text-white font-medium mb-2">Larmor Automobile</p>
              <p className="flex items-center gap-2 mb-1 text-[#A1A1AA]">
                <Phone className="w-4 h-4 text-[#DC2626]" />
                <a href="tel:0685581397" className="hover:text-white transition-colors">06 85 58 13 97</a>
              </p>
              <p className="flex items-center gap-2 text-[#A1A1AA]">
                <MapPin className="w-4 h-4 text-[#DC2626]" />
                <span>Rue des Artisans, Zone de Kervern, 56570 Locmiquélic</span>
              </p>
              <p className="flex items-center gap-2 mt-2 text-[#A1A1AA]">
                <Clock className="w-4 h-4 text-[#DC2626]" />
                <span>Lundi - Samedi : 9h - 19h</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper components for routing
const ConditionsPage = () => <LegalPage pageType="cgv" />;
const WarrantyPage = () => <LegalPage pageType="warranty" />;

// ==================== RESERVATION SUCCESS PAGE ====================
const ReservationSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [reservationData, setReservationData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) { navigate('/'); return; }

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) { setStatus('timeout'); return; }
      try {
        const response = await axios.get(`${API}/reservations/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setReservationData(response.data);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (e) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    };
    pollStatus();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center noise-overlay">
      <div className="max-w-lg mx-auto px-4 md:px-6 text-center animate-fade-in">
        {status === 'loading' && (
          <>
            <div className="spinner mx-auto mb-6" />
            <h1 className="font-heading text-2xl text-white mb-4">Vérification du paiement...</h1>
            <p className="text-[#A1A1AA]">Veuillez patienter</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-[#DC2626] rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-3xl text-white mb-4" data-testid="success-title">Réservation confirmée !</h1>
            <p className="text-[#A1A1AA] mb-4">Vos arrhes de 300 € ont bien été reçues. Nous vous contacterons très rapidement.</p>
            <div className="bg-[#111] p-4 border border-[#22C55E]/30 mb-8">
              <p className="text-[#22C55E] text-sm">Vous disposez de 7 jours pour vous rétracter et être remboursé intégralement. Passé ce délai, les arrhes ne seront pas remboursées.</p>
            </div>
            {reservationData?.metadata && (
              <div className="bg-[#111] p-6 border border-[#27272A] mb-8 text-left">
                <p className="label-red mb-2">Détails</p>
                <p className="text-white">{reservationData.metadata.vehicle_brand} {reservationData.metadata.vehicle_model}</p>
                <p className="text-[#71717A] text-sm mt-1">{reservationData.metadata.customer_name}</p>
              </div>
            )}
            <Link to="/" className="btn-primary inline-block" data-testid="back-home-btn">Retour à l'accueil</Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-600 rounded-full flex items-center justify-center"><X className="w-10 h-10 text-white" /></div>
            <h1 className="font-heading text-3xl text-white mb-4">Session expirée</h1>
            <p className="text-[#A1A1AA] mb-8">Votre session de paiement a expiré. Veuillez réessayer.</p>
            <Link to="/" className="btn-primary inline-block">Retour au catalogue</Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-[#71717A] rounded-full flex items-center justify-center"><Loader2 className="w-10 h-10 text-white" /></div>
            <h1 className="font-heading text-3xl text-white mb-4">Vérification en cours</h1>
            <p className="text-[#A1A1AA] mb-8">Nous vous contacterons par email pour confirmer votre réservation.</p>
            <Link to="/" className="btn-primary inline-block">Retour à l'accueil</Link>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== LOGIN PAGE ====================
const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Clean email input
    const cleanEmail = email.trim().toLowerCase();
    
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email: cleanEmail, 
        password: password 
      });
      login(response.data.access_token, response.data.email, response.data.name, response.data.role);
      toast.success('Connexion réussie');
      // Redirect based on role
      if (response.data.role === 'client') {
        navigate('/');
      } else {
        navigate('/admin');
      }
    } catch (e) {
      console.error('Login error:', e.response?.data);
      toast.error(e.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center noise-overlay">
      <div className="max-w-md w-full mx-4 animate-fade-in">
        <div className="bg-[#111] border border-[#27272A] p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#DC2626]/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#DC2626]" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-2">Espace Gestionnaire</h1>
            <p className="text-[#71717A] text-sm">Connectez-vous pour accéder à l'administration</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="label-red block mb-2">Email</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  className="input-field" 
                  placeholder="votre@email.com" 
                  required 
                  data-testid="login-email"
                />
              </div>
              <div>
                <label className="label-red block mb-2">Mot de passe</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="input-field" 
                  placeholder="••••••••" 
                  required 
                  data-testid="login-password"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full mt-8 flex items-center justify-center gap-2" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
              Se connecter
            </button>
          </form>

          <Link to="/forgot-password" className="block text-center mt-4 text-[#DC2626] text-sm hover:underline transition-colors" data-testid="forgot-password-link">
            Mot de passe oublié ?
          </Link>

          <Link to="/" className="block text-center mt-4 text-[#71717A] text-sm hover:text-white transition-colors">
            ← Retour au site
          </Link>
        </div>
      </div>
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// ==================== LEGAL EDITOR ====================
const LegalEditor = () => {
  const [activeLegalTab, setActiveLegalTab] = useState('cgv');
  const [cgvContent, setCgvContent] = useState({ title: '', content: '' });
  const [warrantyContent, setWarrantyContent] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchLegalPages = async () => {
      try {
        const [cgvRes, warrantyRes] = await Promise.all([
          axios.get(`${API}/legal/cgv`),
          axios.get(`${API}/legal/warranty`)
        ]);
        setCgvContent({ title: cgvRes.data.title, content: cgvRes.data.content });
        setWarrantyContent({ title: warrantyRes.data.title, content: warrantyRes.data.content });
      } catch (e) {
        console.error('Error fetching legal pages:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLegalPages();
  }, []);

  const handleSave = async (pageType) => {
    setSaving(true);
    const content = pageType === 'cgv' ? cgvContent : warrantyContent;
    try {
      await axios.put(`${API}/admin/legal/${pageType}`, content, { headers: getAuthHeaders() });
      toast.success('Page mise à jour avec succès');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><div className="spinner" /></div>;
  }

  const currentContent = activeLegalTab === 'cgv' ? cgvContent : warrantyContent;
  const setCurrentContent = activeLegalTab === 'cgv' ? setCgvContent : setWarrantyContent;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-white font-medium">Gestion des pages légales</h3>
          <p className="text-[#71717A] text-sm mt-1">Modifiez les CGV et conditions de garantie</p>
        </div>
        <div className="flex gap-2">
          <a href="/conditions" target="_blank" className="btn-secondary text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Voir CGV
          </a>
          <a href="/warranty" target="_blank" className="btn-secondary text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Voir Garantie
          </a>
        </div>
      </div>

      {/* Sub-tabs for CGV/Warranty */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveLegalTab('cgv')}
          className={`px-4 py-2 text-sm transition-colors ${activeLegalTab === 'cgv' ? 'bg-[#DC2626] text-white' : 'bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#27272A]'}`}
          data-testid="legal-tab-cgv"
        >
          Conditions Générales de Vente
        </button>
        <button
          onClick={() => setActiveLegalTab('warranty')}
          className={`px-4 py-2 text-sm transition-colors ${activeLegalTab === 'warranty' ? 'bg-[#22C55E] text-white' : 'bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#27272A]'}`}
          data-testid="legal-tab-warranty"
        >
          Garantie 6 Mois
        </button>
      </div>

      <div className="bg-[#111] border border-[#27272A] p-6">
        <div className="mb-4">
          <label className="label-red block mb-2">Titre de la page</label>
          <input
            type="text"
            value={currentContent.title}
            onChange={(e) => setCurrentContent({ ...currentContent, title: e.target.value })}
            className="input-field"
            placeholder="Titre..."
            data-testid="legal-title-input"
          />
        </div>

        <div className="mb-6">
          <label className="label-red block mb-2">Contenu (Markdown supporté)</label>
          <div className="text-[#71717A] text-xs mb-2">
            Utilisez ## pour les titres, ### pour les sous-titres, - pour les listes, **texte** pour le gras
          </div>
          <textarea
            value={currentContent.content}
            onChange={(e) => setCurrentContent({ ...currentContent, content: e.target.value })}
            className="w-full bg-[#0A0A0A] text-white border border-[#27272A] px-4 py-3 focus:border-[#DC2626] focus:outline-none min-h-[400px] font-mono text-sm"
            placeholder="Contenu de la page..."
            data-testid="legal-content-input"
          />
        </div>

        <button
          onClick={() => handleSave(activeLegalTab)}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
          data-testid="legal-save-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          Enregistrer les modifications
        </button>
      </div>
    </div>
  );
};

// ==================== CRM CLIENT MANAGER ====================
const ClientManager = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', city: '', postal_code: '', notes: '', source: 'phone', status: 'lead'
  });

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      let url = `${API}/admin/clients`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchClients();
  };

  const handleViewClient = async (clientId) => {
    try {
      const response = await axios.get(`${API}/admin/clients/${clientId}`, { headers: getAuthHeaders() });
      setSelectedClient(response.data);
    } catch (e) {
      toast.error('Erreur lors du chargement du client');
    }
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/admin/clients/${editingClient.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${API}/admin/clients`, formData, { headers: getAuthHeaders() });
        toast.success('Client créé');
      }
      setShowClientForm(false);
      setEditingClient(null);
      setFormData({ name: '', email: '', phone: '', address: '', city: '', postal_code: '', notes: '', source: 'phone', status: 'lead' });
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      notes: client.notes || '',
      source: client.source || 'phone',
      status: client.status || 'lead'
    });
    setShowClientForm(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`, { headers: getAuthHeaders() });
      toast.success('Client supprimé');
      fetchClients();
      if (selectedClient?.id === clientId) setSelectedClient(null);
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients/export/pdf`, { headers: getAuthHeaders() });
      const blob = new Blob([response.data.html_content], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export de ${response.data.total_clients} clients`);
    } catch (e) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      lead: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      prospect: 'bg-[#3B82F6]/20 text-[#3B82F6]',
      customer: 'bg-[#22C55E]/20 text-[#22C55E]'
    };
    const labels = { lead: 'Lead', prospect: 'Prospect', customer: 'Client' };
    return <span className={`px-2 py-1 text-xs rounded ${styles[status] || styles.lead}`}>{labels[status] || status}</span>;
  };

  if (loading) {
    return <div className="flex justify-center py-10"><div className="spinner" /></div>;
  }

  return (
    <div>
      {/* Header & Actions */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h3 className="text-white font-medium">Gestion des Clients (CRM)</h3>
          <p className="text-[#71717A] text-sm">{clients.length} clients au total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditingClient(null); setShowClientForm(true); }} className="btn-primary flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Nouveau client
          </button>
          <button onClick={handleExportPDF} className="btn-secondary flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" /> Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="flex">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom, email, téléphone..."
              className="input-field flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch} className="bg-[#DC2626] text-white px-4 hover:bg-[#B91C1C] transition-colors">
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#1A1A1A] text-white border border-[#27272A] px-3 py-2"
        >
          <option value="all">Tous les statuts</option>
          <option value="lead">Leads</option>
          <option value="prospect">Prospects</option>
          <option value="customer">Clients</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client List */}
        <div className="lg:col-span-2 overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr><th>Client</th><th>Contact</th><th>Statut</th><th>RDV</th><th>Rés.</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#71717A]">Aucun client trouvé</td></tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id} className={selectedClient?.id === client.id ? 'bg-[#1A1A1A]' : ''}>
                    <td>
                      <p className="text-white font-medium">{client.name}</p>
                      <p className="text-[#71717A] text-xs">{client.city || '-'}</p>
                    </td>
                    <td>
                      <p className="text-[#A1A1AA] text-sm">{client.email}</p>
                      <p className="text-[#71717A] text-xs">{client.phone}</p>
                    </td>
                    <td>{getStatusBadge(client.status)}</td>
                    <td className="text-center text-[#A1A1AA]">{client.total_appointments || 0}</td>
                    <td className="text-center text-[#A1A1AA]">{client.total_reservations || 0}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleViewClient(client.id)} className="p-2 text-[#A1A1AA] hover:text-[#3B82F6]" title="Voir"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => handleEditClient(client)} className="p-2 text-[#A1A1AA] hover:text-[#DC2626]" title="Modifier"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteClient(client.id)} className="p-2 text-[#A1A1AA] hover:text-red-500" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Client Detail Panel */}
        <div className="bg-[#111] border border-[#27272A] p-4">
          {selectedClient ? (
            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-white font-medium text-lg">{selectedClient.name}</h4>
                  {getStatusBadge(selectedClient.status)}
                </div>
                <button onClick={() => setSelectedClient(null)} className="text-[#71717A] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3 text-sm mb-6">
                <p className="flex items-center gap-2 text-[#A1A1AA]"><Mail className="w-4 h-4 text-[#DC2626]" /> {selectedClient.email}</p>
                <p className="flex items-center gap-2 text-[#A1A1AA]"><Phone className="w-4 h-4 text-[#DC2626]" /> {selectedClient.phone}</p>
                {selectedClient.city && <p className="flex items-center gap-2 text-[#A1A1AA]"><MapPin className="w-4 h-4 text-[#DC2626]" /> {selectedClient.city} {selectedClient.postal_code}</p>}
              </div>

              {selectedClient.notes && (
                <div className="bg-[#0A0A0A] p-3 mb-4 border border-[#27272A]">
                  <p className="text-[#71717A] text-xs uppercase mb-1">Notes</p>
                  <p className="text-[#A1A1AA] text-sm">{selectedClient.notes}</p>
                </div>
              )}

              {/* History */}
              <div>
                <p className="text-[#71717A] text-xs uppercase mb-2">Historique</p>
                {selectedClient.appointments?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-white text-sm mb-1">RDV ({selectedClient.appointments.length})</p>
                    {selectedClient.appointments.slice(0, 3).map((apt, i) => (
                      <p key={i} className="text-[#71717A] text-xs">• {apt.appointment_date} - {apt.vehicle_brand} {apt.vehicle_model}</p>
                    ))}
                  </div>
                )}
                {selectedClient.reservations?.length > 0 && (
                  <div>
                    <p className="text-white text-sm mb-1">Réservations ({selectedClient.reservations.length})</p>
                    {selectedClient.reservations.slice(0, 3).map((res, i) => (
                      <p key={i} className="text-[#71717A] text-xs">• {res.status} - {res.vehicle_id?.substring(0, 8)}...</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-10">
              <Users className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
              <p className="text-[#71717A]">Sélectionnez un client pour voir ses détails</p>
            </div>
          )}
        </div>
      </div>

      {/* Client Form Modal */}
      {showClientForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-[#27272A] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="label-red mb-1">{editingClient ? 'Modifier' : 'Nouveau'}</p>
                <h3 className="text-white text-xl font-medium">Client</h3>
              </div>
              <button onClick={() => { setShowClientForm(false); setEditingClient(null); }} className="text-[#71717A] hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            
            <form onSubmit={handleSaveClient} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label-red block mb-1">Nom complet *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-field" required />
                </div>
                <div>
                  <label className="label-red block mb-1">Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-field" required />
                </div>
                <div>
                  <label className="label-red block mb-1">Téléphone *</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="input-field" required />
                </div>
                <div className="col-span-2">
                  <label className="label-red block mb-1">Adresse</label>
                  <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="label-red block mb-1">Ville</label>
                  <input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="label-red block mb-1">Code postal</label>
                  <input type="text" value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="label-red block mb-1">Source</label>
                  <select value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})} className="input-field">
                    <option value="website">Site web</option>
                    <option value="phone">Téléphone</option>
                    <option value="walk-in">Sur place</option>
                  </select>
                </div>
                <div>
                  <label className="label-red block mb-1">Statut</label>
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    <option value="lead">Lead</option>
                    <option value="prospect">Prospect</option>
                    <option value="customer">Client</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="label-red block mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="input-field min-h-[80px]" />
                </div>
              </div>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                {editingClient ? 'Mettre à jour' : 'Créer le client'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN PAGE ====================
const AdminPage = () => {
  const { logout, email, userName, userRole, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('vehicles');
  const [vehicleFilter, setVehicleFilter] = useState('all'); // 'all', 'online', 'offline'
  const [searchQuery, setSearchQuery] = useState(''); // Search by immatriculation
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoManagerVehicle, setPhotoManagerVehicle] = useState(null); // Vehicle for photo management dialog
  const [showVehicleOrderManager, setShowVehicleOrderManager] = useState(false); // Vehicle order manager dialog

  const [formData, setFormData] = useState({
    brand: '', model: '', version: '', price: '', mileage: '', year: '',
    fuel: 'Essence', transmission: 'Manuelle', power: '', color: '',
    options: '', description: '', images: '', immatriculation: '',
    doors: '', previous_owners: '', vehicle_origin: '', 
    last_inspection_date: '', last_service_date: '',
    market_price: '', trade_in_price: '', recent_fees: '',
    is_company_vehicle: false, guaranteed_mileage: false
  });

  const [userFormData, setUserFormData] = useState({
    email: '', password: '', name: '', phone: '', role: 'client'
  });

  const [syncingAutogestion, setSyncingAutogestion] = useState(false);
  const [autogestionStatus, setAutogestionStatus] = useState(null);

  // Auto-Gestion sync function
  const syncFromAutogestion = async () => {
    if (!window.confirm('Cette action va remplacer tous vos véhicules par ceux de votre vitrine Auto-Gestion. Continuer ?')) {
      return;
    }
    
    setSyncingAutogestion(true);
    try {
      const response = await axios.get(`${API}/autogestion/sync`, { headers: getAuthHeaders() });
      toast.success(`${response.data.synced} véhicule(s) synchronisé(s) depuis Auto-Gestion`);
      fetchData(); // Refresh data
      
      // Update status
      const statusResponse = await axios.get(`${API}/autogestion/status`);
      setAutogestionStatus(statusResponse.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncingAutogestion(false);
    }
  };

  // Fetch Auto-Gestion status on load
  useEffect(() => {
    const fetchAutogestionStatus = async () => {
      try {
        const response = await axios.get(`${API}/autogestion/status`);
        setAutogestionStatus(response.data);
      } catch (e) {
        console.log('Auto-Gestion status not available');
      }
    };
    fetchAutogestionStatus();
  }, []);
  
  // Filter vehicles based on visibility status and search
  const filteredVehicles = vehicles.filter(v => {
    // Filter by status
    if (vehicleFilter === 'online' && v.is_online !== true) return false;
    if (vehicleFilter === 'offline' && v.is_online !== false) return false;
    
    // Filter by search (immatriculation, brand, model)
    if (searchQuery) {
      const query = searchQuery.toLowerCase().replace(/-/g, '').replace(/ /g, '');
      const immat = (v.immatriculation || '').toLowerCase().replace(/-/g, '').replace(/ /g, '');
      const brand = (v.brand || '').toLowerCase();
      const model = (v.model || '').toLowerCase();
      if (!immat.includes(query) && !brand.includes(query) && !model.includes(query)) {
        return false;
      }
    }
    
    return true;
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [vehiclesRes, reservationsRes, appointmentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/vehicles?show_all=true`),
        axios.get(`${API}/reservations`, { headers }),
        axios.get(`${API}/appointments`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setVehicles(vehiclesRes.data);
      setReservations(reservationsRes.data);
      setAppointments(appointmentsRes.data);
      setStats(statsRes.data);
      
      // Fetch users if admin
      try {
        const usersRes = await axios.get(`${API}/admin/users`, { headers });
        setUsers(usersRes.data.users || []);
      } catch (e) {
        // Users endpoint may fail for non-admins
      }
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        navigate('/login');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  const resetForm = () => {
    setFormData({ 
      brand: '', model: '', version: '', price: '', mileage: '', year: '', 
      fuel: 'Essence', transmission: 'Manuelle', power: '', color: '', 
      options: '', description: '', images: '', immatriculation: '',
      doors: '', previous_owners: '', vehicle_origin: '',
      last_inspection_date: '', last_service_date: '',
      market_price: '', trade_in_price: '', recent_fees: '',
      is_company_vehicle: false, guaranteed_mileage: false
    });
    setEditingVehicle(null);
  };

  const resetUserForm = () => {
    setUserFormData({ email: '', password: '', name: '', phone: '', role: 'client' });
    setEditingUser(null);
  };

  // Upload photos for a vehicle
  const handlePhotoUpload = async (vehicleId, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    const formDataUpload = new FormData();
    for (let file of files) {
      formDataUpload.append('files', file);
    }
    
    try {
      const response = await axios.post(`${API}/vehicles/${vehicleId}/photos`, formDataUpload, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchData(); // Refresh vehicles
    } catch (e) {
      toast.error('Erreur lors de l\'upload des photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Delete photo from vehicle
  const handleDeletePhoto = async (vehicleId, photoUrl) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}/photos?photo_url=${encodeURIComponent(photoUrl)}`, { headers: getAuthHeaders() });
      toast.success('Photo supprimée');
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Reorder photos - move photo to specific position
  const handleReorderPhotos = async (vehicleId, newOrder) => {
    try {
      await axios.put(`${API}/vehicles/${vehicleId}/photos/reorder`, { images: newOrder }, { headers: getAuthHeaders() });
      // Update local state immediately
      setVehicles(prev => prev.map(v => v.id === vehicleId ? {...v, images: newOrder} : v));
      // Update photo manager if open
      setPhotoManagerVehicle(prev => prev && prev.id === vehicleId ? {...prev, images: newOrder} : prev);
      toast.success('Ordre des photos mis à jour');
    } catch (e) {
      toast.error('Erreur lors du réordonnancement');
    }
  };

  // Set photo as cover (move to first position)
  const handleSetCover = async (vehicleId, photoUrl) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;
    
    const currentImages = vehicle.images || [];
    const newOrder = [photoUrl, ...currentImages.filter(img => img !== photoUrl)];
    await handleReorderPhotos(vehicleId, newOrder);
  };

  // Move photo up in order
  const handleMovePhotoUp = async (vehicleId, photoUrl) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;
    
    const currentImages = [...(vehicle.images || [])];
    const index = currentImages.indexOf(photoUrl);
    if (index <= 0) return;
    
    // Swap with previous
    [currentImages[index - 1], currentImages[index]] = [currentImages[index], currentImages[index - 1]];
    await handleReorderPhotos(vehicleId, currentImages);
  };

  // Move photo down in order
  const handleMovePhotoDown = async (vehicleId, photoUrl) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;
    
    const currentImages = [...(vehicle.images || [])];
    const index = currentImages.indexOf(photoUrl);
    if (index < 0 || index >= currentImages.length - 1) return;
    
    // Swap with next
    [currentImages[index], currentImages[index + 1]] = [currentImages[index + 1], currentImages[index]];
    await handleReorderPhotos(vehicleId, currentImages);
  };

  // Reorder vehicles for public display
  const handleReorderVehicles = async (vehicleIds) => {
    try {
      await axios.put(`${API}/vehicles/reorder`, { vehicle_ids: vehicleIds }, { headers: getAuthHeaders() });
      // Update local state with new order
      const reorderedVehicles = vehicleIds.map((id, index) => {
        const vehicle = vehicles.find(v => v.id === id);
        return vehicle ? { ...vehicle, display_order: index } : null;
      }).filter(Boolean);
      // Add any vehicles not in the reorder list
      const otherVehicles = vehicles.filter(v => !vehicleIds.includes(v.id));
      setVehicles([...reorderedVehicles, ...otherVehicles]);
      toast.success('Ordre des véhicules mis à jour');
    } catch (e) {
      toast.error('Erreur lors du réordonnancement');
    }
  };

  // Move vehicle up in display order
  const handleMoveVehicleUp = async (vehicleId) => {
    const onlineVehicles = vehicles.filter(v => v.is_online).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const index = onlineVehicles.findIndex(v => v.id === vehicleId);
    if (index <= 0) return;
    
    // Swap positions
    const newOrder = [...onlineVehicles];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    await handleReorderVehicles(newOrder.map(v => v.id));
  };

  // Move vehicle down in display order
  const handleMoveVehicleDown = async (vehicleId) => {
    const onlineVehicles = vehicles.filter(v => v.is_online).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const index = onlineVehicles.findIndex(v => v.id === vehicleId);
    if (index < 0 || index >= onlineVehicles.length - 1) return;
    
    // Swap positions
    const newOrder = [...onlineVehicles];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    await handleReorderVehicles(newOrder.map(v => v.id));
  };

  const toggleVehicleOnline = async (vehicleId) => {
    try {
      const response = await axios.put(`${API}/vehicles/${vehicleId}/toggle-online`, {}, { headers: getAuthHeaders() });
      const newStatus = response.data.is_online;
      setVehicles(vehicles.map(v => v.id === vehicleId ? {...v, is_online: newStatus} : v));
      toast.success(newStatus ? 'Véhicule mis en ligne' : 'Véhicule mis hors ligne');
    } catch (e) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleEdit = (vehicle) => {
    setFormData({
      brand: vehicle.brand, model: vehicle.model, version: vehicle.version,
      price: vehicle.price?.toString() || '', mileage: vehicle.mileage?.toString() || '', year: vehicle.year?.toString() || '',
      fuel: vehicle.fuel || 'Essence', transmission: vehicle.transmission || 'Manuelle', power: vehicle.power || '', color: vehicle.color || '',
      options: vehicle.options?.join(', ') || '', description: vehicle.description || '', images: vehicle.images?.join(', ') || '',
      immatriculation: vehicle.immatriculation || '',
      doors: vehicle.doors?.toString() || '', previous_owners: vehicle.previous_owners?.toString() || '',
      vehicle_origin: vehicle.vehicle_origin || '', last_inspection_date: vehicle.last_inspection_date || '',
      last_service_date: vehicle.last_service_date || '', market_price: vehicle.market_price?.toString() || '',
      trade_in_price: vehicle.trade_in_price?.toString() || '', recent_fees: vehicle.recent_fees || '',
      is_company_vehicle: vehicle.is_company_vehicle || false, guaranteed_mileage: vehicle.guaranteed_mileage || false
    });
    setEditingVehicle(vehicle);
    setShowForm(true);
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { headers: getAuthHeaders() });
      toast.success('Véhicule supprimé');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Annuler ce rendez-vous ? Un email d\'annulation sera envoyé au client.')) return;
    try {
      await axios.put(`${API}/appointments/${appointmentId}/cancel`, {}, { headers: getAuthHeaders() });
      toast.success('Rendez-vous annulé');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Supprimer définitivement ce rendez-vous ?')) return;
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, { headers: getAuthHeaders() });
      toast.success('Rendez-vous supprimé');
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (!window.confirm('Supprimer définitivement cette réservation ? Le véhicule sera remis en vente.')) return;
    try {
      await axios.delete(`${API}/reservations/${reservationId}`, { headers: getAuthHeaders() });
      toast.success('Réservation supprimée');
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const vehicleData = {
      brand: formData.brand, model: formData.model, version: formData.version,
      price: parseFloat(formData.price) || 0, mileage: parseInt(formData.mileage) || 0, year: parseInt(formData.year) || 0,
      fuel: formData.fuel, transmission: formData.transmission, power: formData.power, color: formData.color,
      options: formData.options.split(',').map(o => o.trim()).filter(Boolean),
      description: formData.description,
      images: formData.images.split(',').map(i => i.trim()).filter(Boolean),
      immatriculation: formData.immatriculation || null,
      doors: formData.doors ? parseInt(formData.doors) : null,
      previous_owners: formData.previous_owners ? parseInt(formData.previous_owners) : null,
      vehicle_origin: formData.vehicle_origin || null,
      last_inspection_date: formData.last_inspection_date || null,
      last_service_date: formData.last_service_date || null,
      market_price: formData.market_price ? parseFloat(formData.market_price) : null,
      trade_in_price: formData.trade_in_price ? parseFloat(formData.trade_in_price) : null,
      recent_fees: formData.recent_fees || null,
      is_company_vehicle: formData.is_company_vehicle,
      guaranteed_mileage: formData.guaranteed_mileage
    };

    try {
      const headers = getAuthHeaders();
      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, vehicleData, { headers });
        toast.success('Véhicule mis à jour');
      } else {
        await axios.post(`${API}/vehicles`, vehicleData, { headers });
        toast.success('Véhicule ajouté');
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);

  if (loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen pt-16 md:pt-20 noise-overlay">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-6 md:py-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 md:mb-10">
          <div>
            <p className="label-red mb-2">Administration</p>
            <h1 className="font-heading text-2xl md:text-3xl text-white">Gestion Larmor Automobile</h1>
            <p className="text-[#71717A] text-sm mt-1">
              {userName || email} • <span className={userRole === 'super_admin' ? 'text-[#DC2626]' : 'text-[#22C55E]'}>
                {userRole === 'super_admin' ? 'Super Admin' : userRole === 'admin' ? 'Admin' : 'Client'}
              </span>
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {/* Auto-Gestion Sync Button */}
            <button 
              onClick={syncFromAutogestion} 
              disabled={syncingAutogestion}
              className="btn-secondary flex items-center gap-2 bg-[#F59E0B]/10 border-[#F59E0B]/30 hover:bg-[#F59E0B]/20" 
              data-testid="sync-autogestion-btn" 
              title="Synchroniser depuis Auto-Gestion"
            >
              {syncingAutogestion ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5 text-[#F59E0B]" />}
              <span className="hidden md:inline text-[#F59E0B]">Sync Auto-Gestion</span>
            </button>
            <button onClick={() => setShowVehicleOrderManager(true)} className="btn-secondary flex items-center gap-2" data-testid="order-vehicles-btn" title="Organiser l'ordre d'affichage public">
              <GripVertical className="w-5 h-5" />
              <span className="hidden md:inline">Ordre catalogue</span>
            </button>
            <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-vehicle-btn">
              <Plus className="w-5 h-5" />
              Ajouter
            </button>
            <button onClick={handleLogout} className="btn-secondary flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8 md:mb-10">
            <div className="bg-[#111] p-4 md:p-6 border border-[#27272A]">
              <p className="text-[#71717A] text-xs md:text-sm mb-1">Total Véhicules</p>
              <p className="text-2xl md:text-3xl font-bold text-white">{stats.total_vehicles}</p>
            </div>
            <div className="bg-[#111] p-4 md:p-6 border border-[#27272A]">
              <p className="text-[#71717A] text-xs md:text-sm mb-1">En ligne</p>
              <p className="text-2xl md:text-3xl font-bold text-[#22C55E]">{stats.online_vehicles || 0}</p>
            </div>
            <div className="bg-[#111] p-4 md:p-6 border border-[#27272A]">
              <p className="text-[#71717A] text-xs md:text-sm mb-1">Hors ligne</p>
              <p className="text-2xl md:text-3xl font-bold text-[#71717A]">{stats.offline_vehicles || stats.total_vehicles}</p>
            </div>
            <div className="bg-[#111] p-4 md:p-6 border border-[#27272A]">
              <p className="text-[#71717A] text-xs md:text-sm mb-1">Réservés</p>
              <p className="text-2xl md:text-3xl font-bold text-[#DC2626]">{stats.reserved_vehicles}</p>
            </div>
            <div className="bg-[#111] p-4 md:p-6 border border-[#F59E0B]/30">
              <p className="text-[#F59E0B] text-xs md:text-sm mb-1 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Auto-Gestion
              </p>
              {autogestionStatus?.auto_sync_enabled ? (
                <div>
                  <p className="text-[#22C55E] text-xs flex items-center gap-1">● Sync auto activée</p>
                  {autogestionStatus?.last_sync && (
                    <p className="text-[#71717A] text-xs">Dernière: {new Date(autogestionStatus.last_sync).toLocaleString('fr-FR')}</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-[#71717A]">Non synchronisé</p>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 md:gap-4 mb-6 md:mb-8 border-b border-[#27272A] overflow-x-auto">
          {['vehicles', 'appointments', 'reservations', ...(isSuperAdmin ? ['clients', 'users', 'legal'] : [])].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 md:pb-4 px-2 text-xs md:text-sm tracking-wider whitespace-nowrap transition-colors ${activeTab === tab ? 'text-[#DC2626] border-b-2 border-[#DC2626]' : 'text-[#71717A] hover:text-white'}`}>
              {tab === 'vehicles' ? 'VÉHICULES' : tab === 'appointments' ? 'RDV ESSAIS' : tab === 'reservations' ? 'RÉSERVATIONS' : tab === 'clients' ? 'CLIENTS' : tab === 'users' ? 'UTILISATEURS' : 'CONDITIONS'}
            </button>
          ))}
        </div>

        {activeTab === 'vehicles' && (
          <div>
            {/* Search and Filter */}
            <div className="flex flex-wrap gap-3 mb-4">
              {/* Search by immatriculation */}
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par immatriculation, marque, modèle..."
                  className="input-field w-full"
                  data-testid="vehicle-search"
                />
              </div>
              
              {/* Filter buttons */}
              <div className="flex gap-2" data-testid="vehicle-filter">
                <button 
                  onClick={() => setVehicleFilter('all')} 
                  className={`px-3 py-1.5 text-xs transition-colors ${vehicleFilter === 'all' ? 'bg-[#DC2626] text-white' : 'bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#27272A]'}`}
                  data-testid="filter-all"
                >
                  Tous ({vehicles.length})
                </button>
                <button 
                  onClick={() => setVehicleFilter('online')} 
                  className={`px-3 py-1.5 text-xs transition-colors ${vehicleFilter === 'online' ? 'bg-[#22C55E] text-white' : 'bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#27272A]'}`}
                  data-testid="filter-online"
                >
                  En ligne ({vehicles.filter(v => v.is_online).length})
                </button>
                <button 
                  onClick={() => setVehicleFilter('offline')} 
                  className={`px-3 py-1.5 text-xs transition-colors ${vehicleFilter === 'offline' ? 'bg-[#71717A] text-white' : 'bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#27272A]'}`}
                  data-testid="filter-offline"
                >
                  Hors ligne ({vehicles.filter(v => !v.is_online).length})
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr><th>Véhicule</th><th className="hidden md:table-cell">Immat.</th><th className="hidden md:table-cell">Prix</th><th className="hidden md:table-cell">Année</th><th>Statut</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredVehicles.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-[#71717A]">Aucun véhicule trouvé</td></tr>
                ) : (
                  filteredVehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td>
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className="relative">
                          <img src={getImageUrl(vehicle.images?.[0])} alt="" className="w-12 h-9 md:w-16 md:h-12 object-cover" />
                          {vehicle.images?.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-[#DC2626] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{vehicle.images.length}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm md:text-base">{vehicle.brand} {vehicle.model}</p>
                          <p className="text-[#71717A] text-xs md:hidden">{formatPrice(vehicle.price)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell text-[#F59E0B] font-mono text-xs">{vehicle.immatriculation || '-'}</td>
                    <td className="hidden md:table-cell text-[#DC2626] font-medium">{formatPrice(vehicle.price)}</td>
                    <td className="hidden md:table-cell">{vehicle.year}</td>
                    <td>
                      <div className="flex flex-col gap-1">
                        {vehicle.is_reserved ? <span className="badge-reserved text-xs">Réservé</span> : <span className="text-[#22C55E] text-xs">Dispo</span>}
                        <button 
                          onClick={() => toggleVehicleOnline(vehicle.id)} 
                          className={`text-xs px-2 py-1 transition-colors ${vehicle.is_online ? 'bg-[#22C55E]/20 text-[#22C55E] hover:bg-[#22C55E]/30' : 'bg-[#71717A]/20 text-[#71717A] hover:bg-[#71717A]/30'}`}
                          title={vehicle.is_online ? 'Cliquer pour mettre hors ligne' : 'Cliquer pour mettre en ligne'}
                        >
                          {vehicle.is_online ? '● En ligne' : '○ Hors ligne'}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 md:gap-2">
                        {/* Photo upload button */}
                        <label 
                          className={`p-2 cursor-pointer ${(vehicle.images?.length || 0) >= 10 ? 'text-[#71717A] cursor-not-allowed' : 'text-[#A1A1AA] hover:text-[#3B82F6]'}`} 
                          title={(vehicle.images?.length || 0) >= 10 ? 'Maximum 10 photos atteint' : `Ajouter des photos (${vehicle.images?.length || 0}/10)`}
                        >
                          <input
                            type="file"
                            multiple
                            accept="image/jpeg,image/png"
                            className="hidden"
                            onChange={(e) => handlePhotoUpload(vehicle.id, e.target.files)}
                            disabled={uploadingPhotos || (vehicle.images?.length || 0) >= 10}
                          />
                          {uploadingPhotos ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </label>
                        {/* Photo manager button - only show if has photos */}
                        {vehicle.images?.length > 0 && (
                          <button 
                            onClick={() => setPhotoManagerVehicle(vehicle)} 
                            className="p-2 text-[#A1A1AA] hover:text-[#F59E0B]"
                            title="Gérer les photos et définir la couverture"
                          >
                            <Image className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => handleEdit(vehicle)} className="p-2 text-[#A1A1AA] hover:text-[#DC2626]"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(vehicle.id)} className="p-2 text-[#A1A1AA] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead><tr><th>Client</th><th>Contact</th><th>Véhicule</th><th>Date/Heure</th><th>Statut</th><th>Actions</th></tr></thead>
              <tbody>
                {appointments.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-[#71717A]">Aucun rendez-vous</td></tr>
                ) : (
                  appointments.map((apt) => (
                    <tr key={apt.id}>
                      <td><p className="text-white text-sm font-medium">{apt.customer_name}</p></td>
                      <td>
                        <a href={`tel:${apt.customer_phone}`} className="text-[#22C55E] hover:underline text-sm block">{apt.customer_phone}</a>
                        <a href={`mailto:${apt.customer_email}`} className="text-[#3B82F6] hover:underline text-xs block mt-0.5">{apt.customer_email}</a>
                      </td>
                      <td className="text-sm">{apt.vehicle_brand} {apt.vehicle_model}</td>
                      <td><p className="text-white text-sm">{new Date(apt.appointment_date).toLocaleDateString('fr-FR')}</p><p className="text-[#DC2626] text-xs font-medium">{apt.appointment_time}</p></td>
                      <td>{apt.status === 'confirmed' ? <span className="px-2 py-1 text-xs rounded bg-[#22C55E]/20 text-[#22C55E]">Confirmé</span> : <span className="px-2 py-1 text-xs rounded bg-[#71717A]/20 text-[#71717A]">Annulé</span>}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {apt.status === 'confirmed' && (
                            <button onClick={() => handleCancelAppointment(apt.id)} className="text-[#F59E0B] hover:text-[#F59E0B]/80 text-xs px-2 py-1 border border-[#F59E0B]/30 rounded hover:bg-[#F59E0B]/10 transition-colors" data-testid={`cancel-apt-${apt.id}`}>Annuler</button>
                          )}
                          <button onClick={() => handleDeleteAppointment(apt.id)} className="p-1.5 text-[#A1A1AA] hover:text-red-500 transition-colors" title="Supprimer définitivement" data-testid={`delete-apt-${apt.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'reservations' && (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead><tr><th>Client</th><th>Contact</th><th>Véhicule</th><th>Statut</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {reservations.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-[#71717A]">Aucune réservation</td></tr>
                ) : (
                  reservations.map((res) => (
                    <tr key={res.id}>
                      <td className="text-white text-sm font-medium">{res.customer_name}</td>
                      <td>
                        <a href={`tel:${res.customer_phone}`} className="text-[#22C55E] hover:underline text-sm block">{res.customer_phone}</a>
                        <a href={`mailto:${res.customer_email}`} className="text-[#3B82F6] hover:underline text-xs block mt-0.5">{res.customer_email}</a>
                      </td>
                      <td className="text-sm">{res.vehicle_brand || ''} {res.vehicle_model || ''}</td>
                      <td>{res.status === 'confirmed' ? <span className="px-2 py-1 text-xs rounded bg-[#22C55E]/20 text-[#22C55E]">Confirmée</span> : res.status === 'pending' ? <span className="px-2 py-1 text-xs rounded bg-[#F59E0B]/20 text-[#F59E0B]">En attente</span> : <span className="px-2 py-1 text-xs rounded bg-[#71717A]/20 text-[#71717A]">{res.status}</span>}</td>
                      <td className="text-sm">{new Date(res.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <button onClick={() => handleDeleteReservation(res.id)} className="p-1.5 text-[#A1A1AA] hover:text-red-500 transition-colors" title="Supprimer définitivement" data-testid={`delete-res-${res.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'clients' && isSuperAdmin && (
          <ClientManager />
        )}

        {activeTab === 'users' && isSuperAdmin && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-medium">Gestion des utilisateurs</h3>
              <button onClick={() => { resetUserForm(); setShowUserForm(true); }} className="btn-primary flex items-center gap-2 text-sm" data-testid="add-user-btn">
                <Plus className="w-4 h-4" />
                Nouvel utilisateur
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Utilisateur</th><th>Email</th><th>Rôle</th><th>Statut</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-[#71717A]">Aucun utilisateur</td></tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td className="text-white text-sm">{user.name}</td>
                        <td className="text-sm">{user.email}</td>
                        <td>
                          <span className={`text-xs px-2 py-1 ${user.role === 'super_admin' ? 'bg-[#DC2626]/20 text-[#DC2626]' : user.role === 'admin' ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-[#71717A]/20 text-[#A1A1AA]'}`}>
                            {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Client'}
                          </span>
                        </td>
                        <td>{user.is_active ? <span className="text-[#22C55E] text-xs">Actif</span> : <span className="text-[#71717A] text-xs">Inactif</span>}</td>
                        <td>
                          {user.role !== 'super_admin' && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingUser(user); setUserFormData({ email: user.email, password: '', name: user.name, phone: user.phone || '', role: user.role }); setShowUserForm(true); }} className="p-2 text-[#A1A1AA] hover:text-[#DC2626]" title="Modifier"><Pencil className="w-4 h-4" /></button>
                              <button onClick={async () => { if (window.confirm('Envoyer un email de réinitialisation ?')) { try { await axios.post(`${API}/admin/users/${user.id}/reset-password`, {}, { headers: getAuthHeaders() }); toast.success('Email envoyé'); } catch { toast.error('Erreur'); } } }} className="p-2 text-[#A1A1AA] hover:text-[#F59E0B]" title="Reset MDP"><Lock className="w-4 h-4" /></button>
                              <button onClick={async () => { if (window.confirm('Supprimer cet utilisateur ?')) { try { await axios.delete(`${API}/admin/users/${user.id}`, { headers: getAuthHeaders() }); toast.success('Utilisateur supprimé'); fetchData(); } catch { toast.error('Erreur'); } } }} className="p-2 text-[#A1A1AA] hover:text-red-500" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'legal' && isSuperAdmin && (
          <LegalEditor />
        )}
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <div className="dialog-overlay" onClick={() => setShowUserForm(false)}>
          <div className="dialog-content max-w-md animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="label-red mb-2">{editingUser ? 'Modifier' : 'Nouvel'}</p>
                <h2 className="font-heading text-xl text-white">{editingUser ? 'Modifier' : 'Créer'} un utilisateur</h2>
              </div>
              <button onClick={() => setShowUserForm(false)} className="text-[#71717A] hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setFormLoading(true);
              try {
                const headers = getAuthHeaders();
                if (editingUser) {
                  const updateData = { name: userFormData.name, phone: userFormData.phone, role: userFormData.role };
                  await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, { headers });
                  toast.success('Utilisateur mis à jour');
                } else {
                  await axios.post(`${API}/admin/users`, userFormData, { headers });
                  toast.success('Utilisateur créé');
                }
                setShowUserForm(false);
                resetUserForm();
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur');
              } finally {
                setFormLoading(false);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="label-red block mb-2">Nom *</label>
                  <input type="text" value={userFormData.name} onChange={(e) => setUserFormData({...userFormData, name: e.target.value})} className="input-field" placeholder="Jean Dupont" required />
                </div>
                <div>
                  <label className="label-red block mb-2">Email *</label>
                  <input type="email" value={userFormData.email} onChange={(e) => setUserFormData({...userFormData, email: e.target.value})} className="input-field" placeholder="jean@email.com" required disabled={!!editingUser} />
                </div>
                {!editingUser && (
                  <div>
                    <label className="label-red block mb-2">Mot de passe *</label>
                    <input type="password" value={userFormData.password} onChange={(e) => setUserFormData({...userFormData, password: e.target.value})} className="input-field" placeholder="Min 8 caractères" required minLength={8} />
                  </div>
                )}
                <div>
                  <label className="label-red block mb-2">Téléphone</label>
                  <input type="tel" value={userFormData.phone} onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})} className="input-field" placeholder="06 12 34 56 78" />
                </div>
                <div>
                  <label className="label-red block mb-2">Rôle *</label>
                  <select value={userFormData.role} onChange={(e) => setUserFormData({...userFormData, role: e.target.value})} className="w-full bg-[#1A1A1A] text-white border-b border-[#27272A] py-4">
                    <option value="client">Client</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setShowUserForm(false)} className="btn-secondary flex-1">Annuler</button>
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <div className="dialog-content max-w-3xl max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="label-red mb-2">{editingVehicle ? 'Modifier' : 'Nouveau'}</p>
                <h2 className="font-heading text-xl md:text-2xl text-white">{editingVehicle ? 'Modifier' : 'Ajouter'} un véhicule</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#71717A] hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Section principale */}
              <p className="text-[#DC2626] text-xs font-medium mb-3 uppercase tracking-wider">Informations principales</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="label-red block mb-2">Marque *</label><input type="text" value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} className="input-field" placeholder="Mercedes" required /></div>
                <div><label className="label-red block mb-2">Modèle *</label><input type="text" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} className="input-field" placeholder="Classe S" required /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="label-red block mb-2">Version/Motorisation *</label><input type="text" value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} className="input-field" placeholder="63 AMG" required /></div>
                <div><label className="label-red block mb-2">Immatriculation</label><input type="text" value={formData.immatriculation} onChange={(e) => setFormData({...formData, immatriculation: e.target.value})} className="input-field" placeholder="AB-123-CD" /></div>
              </div>
              
              {/* Prix et kilométrage */}
              <p className="text-[#DC2626] text-xs font-medium mb-3 mt-6 uppercase tracking-wider">Prix et kilométrage</p>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div><label className="label-red block mb-2">Prix (€) *</label><input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="input-field" required /></div>
                <div><label className="label-red block mb-2">Prix marché (€)</label><input type="number" value={formData.market_price} onChange={(e) => setFormData({...formData, market_price: e.target.value})} className="input-field" /></div>
                <div><label className="label-red block mb-2">Prix reprise (€)</label><input type="number" value={formData.trade_in_price} onChange={(e) => setFormData({...formData, trade_in_price: e.target.value})} className="input-field" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="label-red block mb-2">Kilométrage *</label><input type="number" value={formData.mileage} onChange={(e) => setFormData({...formData, mileage: e.target.value})} className="input-field" required /></div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.guaranteed_mileage} onChange={(e) => setFormData({...formData, guaranteed_mileage: e.target.checked})} className="w-5 h-5 accent-[#DC2626]" />
                    <span className="text-white">Kilométrage garanti</span>
                  </label>
                </div>
              </div>
              
              {/* Caractéristiques techniques */}
              <p className="text-[#DC2626] text-xs font-medium mb-3 mt-6 uppercase tracking-wider">Caractéristiques techniques</p>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div><label className="label-red block mb-2">Année *</label><input type="number" value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} className="input-field" required /></div>
                <div><label className="label-red block mb-2">Carburant</label><select value={formData.fuel} onChange={(e) => setFormData({...formData, fuel: e.target.value})} className="w-full bg-[#1A1A1A] text-white border-b border-[#27272A] py-4"><option>Essence</option><option>Diesel</option><option>Hybride</option><option>Électrique</option><option>GPL</option></select></div>
                <div><label className="label-red block mb-2">Boîte</label><select value={formData.transmission} onChange={(e) => setFormData({...formData, transmission: e.target.value})} className="w-full bg-[#1A1A1A] text-white border-b border-[#27272A] py-4"><option>Manuelle</option><option>Automatique</option><option>Séquentielle</option></select></div>
                <div><label className="label-red block mb-2">Portes</label><input type="number" value={formData.doors} onChange={(e) => setFormData({...formData, doors: e.target.value})} className="input-field" placeholder="5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="label-red block mb-2">Puissance *</label><input type="text" value={formData.power} onChange={(e) => setFormData({...formData, power: e.target.value})} className="input-field" placeholder="150 ch" required /></div>
                <div><label className="label-red block mb-2">Couleur *</label><input type="text" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} className="input-field" placeholder="Noir" required /></div>
              </div>
              
              {/* Historique */}
              <p className="text-[#DC2626] text-xs font-medium mb-3 mt-6 uppercase tracking-wider">Historique du véhicule</p>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div><label className="label-red block mb-2">Nb. de mains</label><input type="number" value={formData.previous_owners} onChange={(e) => setFormData({...formData, previous_owners: e.target.value})} className="input-field" placeholder="2" /></div>
                <div><label className="label-red block mb-2">Origine</label><input type="text" value={formData.vehicle_origin} onChange={(e) => setFormData({...formData, vehicle_origin: e.target.value})} className="input-field" placeholder="France" /></div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_company_vehicle} onChange={(e) => setFormData({...formData, is_company_vehicle: e.target.checked})} className="w-5 h-5 accent-[#DC2626]" />
                    <span className="text-white">Véhicule de société</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="label-red block mb-2">Dernier contrôle technique</label><input type="date" value={formData.last_inspection_date} onChange={(e) => setFormData({...formData, last_inspection_date: e.target.value})} className="input-field" /></div>
                <div><label className="label-red block mb-2">Dernière révision</label><input type="date" value={formData.last_service_date} onChange={(e) => setFormData({...formData, last_service_date: e.target.value})} className="input-field" /></div>
              </div>
              <div className="mb-4"><label className="label-red block mb-2">Frais récents</label><input type="text" value={formData.recent_fees} onChange={(e) => setFormData({...formData, recent_fees: e.target.value})} className="input-field" placeholder="Pneus neufs, freins..." /></div>
              
              {/* Description et options */}
              <p className="text-[#DC2626] text-xs font-medium mb-3 mt-6 uppercase tracking-wider">Description et équipements</p>
              <div className="mb-4"><label className="label-red block mb-2">Options (séparées par virgules)</label><input type="text" value={formData.options} onChange={(e) => setFormData({...formData, options: e.target.value})} className="input-field" placeholder="GPS, Cuir, Toit ouvrant..." /></div>
              <div className="mb-4"><label className="label-red block mb-2">Description *</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-field min-h-[100px]" required /></div>
              <div className="mb-6"><label className="label-red block mb-2">URLs images (séparées par virgules)</label><input type="text" value={formData.images} onChange={(e) => setFormData({...formData, images: e.target.value})} className="input-field" placeholder="https://..." /><p className="text-[#71717A] text-xs mt-1">Utilisez le bouton + dans la liste pour uploader des photos</p></div>
              
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">Annuler</button>
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {editingVehicle ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Manager Dialog */}
      {photoManagerVehicle && (
        <div className="dialog-overlay" onClick={() => setPhotoManagerVehicle(null)}>
          <div className="dialog-content max-w-3xl max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="label-red mb-2">Gérer les photos</p>
                <h2 className="font-heading text-xl md:text-2xl text-white">{photoManagerVehicle.brand} {photoManagerVehicle.model}</h2>
                <p className="text-[#71717A] text-sm mt-1">{photoManagerVehicle.images?.length || 0} photo(s) • La première est la couverture</p>
              </div>
              <button onClick={() => setPhotoManagerVehicle(null)} className="text-[#71717A] hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            {/* Upload new photos */}
            <div className="mb-6">
              <label 
                className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-[#27272A] hover:border-[#DC2626] cursor-pointer transition-colors ${(photoManagerVehicle.images?.length || 0) >= 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => {
                    handlePhotoUpload(photoManagerVehicle.id, e.target.files);
                    // Refresh vehicle data after upload
                    setTimeout(() => {
                      const updatedVehicle = vehicles.find(v => v.id === photoManagerVehicle.id);
                      if (updatedVehicle) setPhotoManagerVehicle(updatedVehicle);
                    }, 2000);
                  }}
                  disabled={uploadingPhotos || (photoManagerVehicle.images?.length || 0) >= 10}
                />
                {uploadingPhotos ? (
                  <><Loader2 className="w-5 h-5 animate-spin text-[#DC2626]" /> <span className="text-white">Upload en cours...</span></>
                ) : (
                  <><Plus className="w-5 h-5 text-[#DC2626]" /> <span className="text-white">Ajouter des photos ({photoManagerVehicle.images?.length || 0}/10)</span></>
                )}
              </label>
            </div>

            {/* Photos grid with reordering */}
            <div className="space-y-3">
              {photoManagerVehicle.images?.map((img, index) => (
                <div 
                  key={img} 
                  className={`flex items-center gap-4 p-3 border ${index === 0 ? 'border-[#F59E0B] bg-[#F59E0B]/10' : 'border-[#27272A] bg-[#1A1A1A]'}`}
                >
                  {/* Photo thumbnail */}
                  <div className="relative w-24 h-18 md:w-32 md:h-24 flex-shrink-0">
                    <img 
                      src={getImageUrl(img)} 
                      alt={`Photo ${index + 1}`} 
                      className="w-full h-full object-cover"
                    />
                    {index === 0 && (
                      <div className="absolute top-1 left-1 bg-[#F59E0B] text-black text-[10px] px-1.5 py-0.5 font-bold flex items-center gap-1">
                        <Star className="w-3 h-3" /> COUVERTURE
                      </div>
                    )}
                  </div>

                  {/* Photo info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">Photo {index + 1}</p>
                    <p className="text-[#71717A] text-xs truncate">{img}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Set as cover */}
                    {index !== 0 && (
                      <button 
                        onClick={() => handleSetCover(photoManagerVehicle.id, img)}
                        className="p-2 text-[#A1A1AA] hover:text-[#F59E0B] transition-colors"
                        title="Définir comme couverture"
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    {/* Move up */}
                    <button 
                      onClick={() => handleMovePhotoUp(photoManagerVehicle.id, img)}
                      className={`p-2 transition-colors ${index === 0 ? 'text-[#27272A] cursor-not-allowed' : 'text-[#A1A1AA] hover:text-white'}`}
                      disabled={index === 0}
                      title="Monter"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    {/* Move down */}
                    <button 
                      onClick={() => handleMovePhotoDown(photoManagerVehicle.id, img)}
                      className={`p-2 transition-colors ${index === photoManagerVehicle.images.length - 1 ? 'text-[#27272A] cursor-not-allowed' : 'text-[#A1A1AA] hover:text-white'}`}
                      disabled={index === photoManagerVehicle.images.length - 1}
                      title="Descendre"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    {/* Delete */}
                    <button 
                      onClick={async () => {
                        await handleDeletePhoto(photoManagerVehicle.id, img);
                        // Update local state
                        const newImages = photoManagerVehicle.images.filter(i => i !== img);
                        setPhotoManagerVehicle({...photoManagerVehicle, images: newImages});
                        if (newImages.length === 0) setPhotoManagerVehicle(null);
                      }}
                      className="p-2 text-[#A1A1AA] hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Help text */}
            <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#27272A]">
              <p className="text-[#71717A] text-sm">
                <Star className="w-4 h-4 inline text-[#F59E0B] mr-1" /> 
                La photo en <span className="text-[#F59E0B]">première position</span> est la couverture visible sur le catalogue.
                Utilisez les flèches pour réordonner les photos.
              </p>
            </div>

            <div className="mt-6">
              <button onClick={() => setPhotoManagerVehicle(null)} className="btn-primary w-full">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Order Manager Dialog */}
      {showVehicleOrderManager && (
        <div className="dialog-overlay" onClick={() => setShowVehicleOrderManager(false)}>
          <div className="dialog-content max-w-3xl max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="label-red mb-2">Ordre du catalogue</p>
                <h2 className="font-heading text-xl md:text-2xl text-white">Organiser l'affichage public</h2>
                <p className="text-[#71717A] text-sm mt-1">Réorganisez les véhicules "En ligne" pour le catalogue public</p>
              </div>
              <button onClick={() => setShowVehicleOrderManager(false)} className="text-[#71717A] hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            {/* Online vehicles list for reordering */}
            <div className="space-y-3">
              {vehicles
                .filter(v => v.is_online)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                .map((vehicle, index, arr) => (
                <div 
                  key={vehicle.id} 
                  className={`flex items-center gap-4 p-3 border ${index === 0 ? 'border-[#22C55E] bg-[#22C55E]/10' : 'border-[#27272A] bg-[#1A1A1A]'}`}
                >
                  {/* Position number */}
                  <div className="w-8 h-8 flex items-center justify-center bg-[#27272A] text-white font-bold text-sm">
                    {index + 1}
                  </div>

                  {/* Vehicle thumbnail */}
                  <div className="w-20 h-14 md:w-24 md:h-16 flex-shrink-0">
                    <img 
                      src={getImageUrl(vehicle.images?.[0])} 
                      alt={`${vehicle.brand} ${vehicle.model}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Vehicle info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm md:text-base">{vehicle.brand} {vehicle.model}</p>
                    <p className="text-[#DC2626] text-sm">{formatPrice(vehicle.price)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Move up */}
                    <button 
                      onClick={() => handleMoveVehicleUp(vehicle.id)}
                      className={`p-2 transition-colors ${index === 0 ? 'text-[#27272A] cursor-not-allowed' : 'text-[#A1A1AA] hover:text-white'}`}
                      disabled={index === 0}
                      title="Monter"
                    >
                      <ArrowUp className="w-5 h-5" />
                    </button>
                    {/* Move down */}
                    <button 
                      onClick={() => handleMoveVehicleDown(vehicle.id)}
                      className={`p-2 transition-colors ${index === arr.length - 1 ? 'text-[#27272A] cursor-not-allowed' : 'text-[#A1A1AA] hover:text-white'}`}
                      disabled={index === arr.length - 1}
                      title="Descendre"
                    >
                      <ArrowDown className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}

              {vehicles.filter(v => v.is_online).length === 0 && (
                <div className="text-center py-8 text-[#71717A]">
                  <p>Aucun véhicule en ligne</p>
                  <p className="text-sm mt-2">Mettez des véhicules "En ligne" pour les organiser</p>
                </div>
              )}
            </div>

            {/* Help text */}
            <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#27272A]">
              <p className="text-[#71717A] text-sm">
                <GripVertical className="w-4 h-4 inline text-[#22C55E] mr-1" /> 
                Le véhicule en <span className="text-[#22C55E]">position 1</span> apparaît en premier sur le catalogue public.
                Seuls les véhicules "En ligne" sont affichés ici.
              </p>
            </div>

            <div className="mt-6">
              <button onClick={() => setShowVehicleOrderManager(false)} className="btn-primary w-full">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== FORGOT PASSWORD PAGE ====================
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email envoyé avec succès');
    } catch (e) {
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center noise-overlay">
        <div className="max-w-md w-full mx-4 animate-fade-in">
          <div className="bg-[#111] border border-[#27272A] p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#22C55E]/10 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-[#22C55E]" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-4">Email envoyé</h1>
            <p className="text-[#A1A1AA] mb-6">
              Si l'adresse <strong className="text-white">{email}</strong> est associée à un compte, 
              vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
            </p>
            <p className="text-[#71717A] text-sm mb-6">
              Le lien expire dans 1 heure.
            </p>
            <Link to="/login" className="btn-secondary inline-block">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center noise-overlay">
      <div className="max-w-md w-full mx-4 animate-fade-in">
        <div className="bg-[#111] border border-[#27272A] p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#DC2626]/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#DC2626]" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-2">Mot de passe oublié</h1>
            <p className="text-[#71717A] text-sm">Entrez votre email pour recevoir un lien de réinitialisation</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="label-red block mb-2">Email</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  className="input-field" 
                  placeholder="votre@email.com" 
                  required 
                  data-testid="forgot-email"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full mt-8 flex items-center justify-center gap-2" 
              disabled={loading}
              data-testid="forgot-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              Envoyer le lien
            </button>
          </form>

          <Link to="/login" className="block text-center mt-6 text-[#71717A] text-sm hover:text-white transition-colors">
            ← Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

// ==================== RESET PASSWORD PAGE ====================
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
        setTokenEmail(response.data.email);
      } catch (e) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Mot de passe modifié avec succès');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center noise-overlay">
        <div className="max-w-md w-full mx-4 animate-fade-in">
          <div className="bg-[#111] border border-[#27272A] p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-4">Lien invalide ou expiré</h1>
            <p className="text-[#A1A1AA] mb-6">
              Ce lien de réinitialisation n'est plus valide. Veuillez faire une nouvelle demande.
            </p>
            <Link to="/forgot-password" className="btn-primary inline-block">
              Nouvelle demande
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center noise-overlay">
        <div className="max-w-md w-full mx-4 animate-fade-in">
          <div className="bg-[#111] border border-[#27272A] p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#22C55E]/10 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-[#22C55E]" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-4">Mot de passe modifié</h1>
            <p className="text-[#A1A1AA] mb-6">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <button onClick={() => navigate('/login')} className="btn-primary" data-testid="go-login-btn">
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 md:pt-20 flex items-center justify-center noise-overlay">
      <div className="max-w-md w-full mx-4 animate-fade-in">
        <div className="bg-[#111] border border-[#27272A] p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#DC2626]/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#DC2626]" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-2">Nouveau mot de passe</h1>
            <p className="text-[#71717A] text-sm">Pour le compte : <span className="text-white">{tokenEmail}</span></p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="label-red block mb-2">Nouveau mot de passe</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="input-field" 
                  placeholder="••••••••" 
                  required
                  minLength={8}
                  data-testid="new-password"
                />
                <p className="text-[#71717A] text-xs mt-2">Minimum 8 caractères</p>
              </div>
              <div>
                <label className="label-red block mb-2">Confirmer le mot de passe</label>
                <input 
                  type="password" 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  className="input-field" 
                  placeholder="••••••••" 
                  required
                  data-testid="confirm-password"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full mt-8 flex items-center justify-center gap-2" 
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              Réinitialiser
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==================== APP ====================
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" theme="dark" richColors />
        <BrowserRouter>
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/vehicle/:id" element={<VehicleDetail />} />
            <Route path="/conditions" element={<ConditionsPage />} />
            <Route path="/warranty" element={<WarrantyPage />} />
            <Route path="/reservation/success" element={<ReservationSuccess />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
